import * as React from 'react'

import { ApplicationType, ConfigKeys, CreditCardStep, Product } from 'even-ts-static'

import { applicationFieldBlurred, applicationFieldChanged } from 'common/actions/application'
import { configCreditCardApplicationTypeSelector, configCreditCardRewardsEnabledSelector } from 'embed/selectors/config'
import { MultiProductDispatch } from 'embed/constants'
import { Fields } from 'embed/constants/fields'
import { MultiProductState } from 'embed/reducers'
import { hybridOfferListSelector, uiSelectedCreditStepSelector } from 'embed/selectors/ui'
import { connect } from 'react-redux'
import { CreditCardCommonForm } from './common/CreditCardCommonForm'
import { CreditCardHybridOfferList } from './CreditCardHybridOfferList'
import { CreditCardIntroContainer } from './Intro/container'
import { CreditCardPreview } from './Preview'
import { CreditCardRemarketingForm } from './RemarketingForm'
import { ConnectedCreditResultsContainer } from './Results/container'
import { CreditStepByStepContainer } from './StepByStep/container'

interface StoreProps {
  applicationType?: ApplicationType
  hasOffer: boolean
  isHybridOfferListEnabled?: boolean
  rewardsEnabled: boolean
  selectedStep: CreditCardStep
}

interface DispatchProps {
  dispatchConsentsToFCRAandTCPA: () => void
}

type Props = StoreProps & DispatchProps

const Render: React.FunctionComponent<Props> = ({
  applicationType,
  dispatchConsentsToFCRAandTCPA,
  hasOffer,
  isHybridOfferListEnabled,
  selectedStep,
}) => {
  // If we're on the remarketing form without an offer, redirect to preview
  if (selectedStep === CreditCardStep.RemarketingForm && hasOffer === false) {
    return <CreditCardPreview />
  }

  switch (selectedStep) {
    case CreditCardStep.Intro:
      return <CreditCardIntroContainer />
    case CreditCardStep.PreApprovalForm:
    case CreditCardStep.PreQualifiedForm:
    case CreditCardStep.PreSelectForm:
      if (applicationType === ApplicationType.StepByStep) {
        return <CreditStepByStepContainer collectFinancialInformation={false} />
      }
      return (
        <CreditCardCommonForm
          collectFinancialInformation={false}
          handleConsentsToFCRAandTCPA={dispatchConsentsToFCRAandTCPA}
        />
      )
    case CreditCardStep.RemarketingForm:
      return <CreditCardRemarketingForm />
    case CreditCardStep.Results:
      return <ConnectedCreditResultsContainer />
    case CreditCardStep.Preview:
      return isHybridOfferListEnabled ? <CreditCardHybridOfferList /> : <CreditCardPreview />
    default:
      return <CreditCardPreview />
  }
}

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchConsentsToFCRAandTCPA: () => {
    dispatch(applicationFieldChanged(Product.CreditCard, Fields.ConsentsToFCRA.key, true)).catch(console.error)
    dispatch(applicationFieldBlurred(Product.CreditCard, Fields.ConsentsToFCRA.key))
    dispatch(applicationFieldChanged(Product.CreditCard, Fields.ConsentsToTCPA.key, true)).catch(console.error)
    dispatch(applicationFieldBlurred(Product.CreditCard, Fields.ConsentsToTCPA.key))
  },
})

const mapStateToProps = (state: MultiProductState): StoreProps => ({
  applicationType: configCreditCardApplicationTypeSelector(state),
  hasOffer:
    // We can have an offer either by query param or selecting one from the preview step
    (state.config[ConfigKeys.Offer] !== undefined || state.rates.selected[Product.CreditCard]?.uuid !== undefined) &&
    // Handle the case where we have an offer ID, but the offer itself fails to load
    state.rates.selected.loadOfferError?.[Product.CreditCard] !== true,
  isHybridOfferListEnabled: hybridOfferListSelector(state),
  rewardsEnabled: configCreditCardRewardsEnabledSelector(state),
  selectedStep: uiSelectedCreditStepSelector(state),
})

export const CreditCardApplicationContainer = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(Render)
